"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  /** Render this when the wrapped subtree throws. Defaults to a
   *  retailer-card-shaped placeholder. */
  fallback?: ReactNode;
  /** Optional label surfaced into console for triage. */
  label?: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Universal error boundary used to wrap per-retailer cards on the
 * comparison page.
 *
 * Before this existed: a single component throwing on null / missing
 * TFP layer data could blank the entire comparison page (or render
 * a stray empty card). The pSEO contract requires every comparison
 * URL to render statically without a JS error, so a fragile child
 * component must not bring down the page.
 *
 * After: when a wrapped child throws, this boundary catches the
 * error, logs it for triage, and renders a clean fallback in place
 * of the broken card. Other retailer cards on the page render
 * normally.
 *
 * The default fallback intentionally mirrors the card shape so the
 * page grid doesn't reflow.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console so prod errors are inspectable via
    // browser devtools / Sentry-style integrations later. Keep this
    // intentionally minimal so a render error inside the boundary
    // itself can't recurse.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <article
          className={
            "group relative h-full overflow-hidden rounded-2xl " +
            "border border-border/60 bg-secondary/30 backdrop-blur-md " +
            "px-5 py-4 sm:px-6 sm:py-5"
          }
          role="alert"
        >
          <p className="text-sm font-medium text-foreground">
            Data unavailable for this retailer
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Something went wrong rendering this offer. Please check the
            retailer directly.
          </p>
        </article>
      );
    }
    return this.props.children;
  }
}
